/**
 * מנוע הביקור (שלב 4) — כללים מפורשים, ללא AI.
 *
 * - **תחולה:** כל סעיף מקבל הכרעה וסיבה בשפת אדם. עובדה לא ידועה בפרופיל
 *   האתר אינה מסננת — הסעיף מוצג, והסיבה אומרת שהמידע חסר.
 * - **סימונים לפי פרק:** פרק תוכנית הבטיחות — תקין / דורש תיקון / לא רלוונטי
 *   לביקור; שאר הפרקים — תקין / לא תקין / לא רלוונטי. **אין ברירת מחדל**:
 *   סעיף בלי סימון הוא ״לא נבדק״.
 * - **הגשה:** אותם כללים שהשרת אוכף ב־`submit_visit`, כדי שהמסך יראה מראש מה
 *   חוסם. השרת הוא הקובע.
 */

import {
  APPLICABILITY_FACTS,
  type AnswerScheme,
  type ApplicabilityRule,
} from './checklistContent.ts';
import { STAGE_KIND_LABEL, STAGE_KINDS, type SiteFacts, type StageKind } from './siteProfile.ts';

export type AnswerValue = 'ok' | 'not_ok' | 'needs_fix' | 'not_relevant';
/** חומרת ממצא (D19, 01.10.2026): מיידי · גבוה · נמוך. */
export type Severity = 'immediate' | 'high' | 'low';
/** ערך כפי שנשמר — snapshot שהוקפא לפני D19 עדיין מכיל `normal`. */
export type StoredSeverity = Severity | 'normal';

/** ממפה ערך שמור לחומרה הנוכחית: `normal` (לפני D19) ← `low`. ערך לא מוכר ← null. */
export function normalizeSeverity(raw: unknown): Severity | null {
  if (raw === 'immediate' || raw === 'high' || raw === 'low') return raw;
  if (raw === 'normal') return 'low';
  return null;
}

export const SCHEME_OPTIONS: Record<AnswerScheme, AnswerValue[]> = {
  plan_single: ['ok', 'needs_fix', 'not_relevant'],
  standard: ['ok', 'not_ok', 'not_relevant'],
};

const ANSWER_LABEL: Record<AnswerScheme, Partial<Record<AnswerValue, string>>> = {
  plan_single: { ok: 'תקין', needs_fix: 'דורש תיקון', not_relevant: 'לא רלוונטי לביקור' },
  standard: { ok: 'תקין', not_ok: 'לא תקין', not_relevant: 'לא רלוונטי' },
};

export const NOT_CHECKED_LABEL = 'לא נבדק';

/**
 * סיבה מהירה ל״לא נבדק״ (D14, RQ-D08) — **לא חובה**, בחירה בנגיעה אחת. נשמרת
 * רק לסעיף שלא סומן; סימון הסעיף מנקה אותה. פירוט נוסף — בשדה ההערה.
 */
export type NotCheckedReason = 'area_inaccessible' | 'work_not_done' | 'other';

export const NOT_CHECKED_REASONS: NotCheckedReason[] = [
  'area_inaccessible',
  'work_not_done',
  'other',
];

export const NOT_CHECKED_REASON_LABEL: Record<NotCheckedReason, string> = {
  area_inaccessible: 'אזור לא נגיש',
  work_not_done: 'העבודה לא בוצעה היום',
  other: 'אחר',
};

/** תווית הסימון; `null` (לא סומן) — ״לא נבדק״. */
export function answerLabel(scheme: AnswerScheme, state: AnswerValue | null): string {
  if (state === null) return NOT_CHECKED_LABEL;
  return ANSWER_LABEL[scheme][state] ?? state;
}

/** ״לא תקין״ ו״דורש תיקון״ — ליקוי. ממצא ממוספר אינו חובה (D23). */
export function isDefect(state: AnswerValue | null): boolean {
  return state === 'not_ok' || state === 'needs_fix';
}

export const SEVERITY_LABEL: Record<Severity, string> = {
  immediate: 'מיידי — סיכון בלתי קביל',
  high: 'גבוה',
  low: 'נמוך',
};

/** תווית קצרה לכפתורים ולטבלאות: מיידי · גבוה · נמוך. */
export const SEVERITY_SHORT_LABEL: Record<Severity, string> = {
  immediate: 'מיידי',
  high: 'גבוה',
  low: 'נמוך',
};

/** סדר בטבלת הליקויים: מיידי, גבוה, נמוך, ובסוף — ללא חומרה (חוסם הגשה). */
export const SEVERITY_ORDER: Array<Severity | null> = ['immediate', 'high', 'low', null];

/** סוג הביקור (D22). */
export type VisitKind = 'periodic' | 'repeat' | 'critical_stage' | 'other';

export const VISIT_KINDS: VisitKind[] = ['periodic', 'repeat', 'critical_stage', 'other'];

export const VISIT_KIND_LABEL: Record<VisitKind, string> = {
  periodic: 'ביקור תקופתי',
  repeat: 'ביקור חוזר',
  critical_stage: 'ביקור בשלב קריטי',
  other: 'אחר',
};

export const VERIFICATION_LABEL = {
  unverified: 'לא מאומת',
  text_verified: 'נוסח המקור אומת — הסעיף לא נבדק מקצועית',
  professionally_reviewed: 'התאמה מקצועית נבדקה',
} as const;

// --- תחולה -------------------------------------------------------------------

export const UNKNOWN_FACT_REASON =
  'המידע בפרופיל האתר חסר (״נדרש בירור״) — הסעיף מוצג כדי שלא ייווצר סינון על בסיס חוסר מידע';

export interface ApplicabilityDecision {
  applicable: boolean;
  reason: string;
}

/**
 * תחולה לפי שלב בנייה (CQ-07, תוכן 0.2) — **כלל מוצר**, לא טענה משפטית, והסיבה אומרת זאת.
 * לא נבחרו שלבים = ״נדרש בירור״ = הפרק מופיע; הבקר מסיר או מחזיר ידנית (D21).
 */
export const NO_STAGES_REASON = 'לא נבחרו שלבים בפתיחת הביקור — הפרק מוצג (כלל מוצר; אפשר להסיר)';

/** תחילת הסיבה של פרק שסונן לפי שלב — כך הדוח מזהה פרק ״לפי שלב״ שלא נכלל (D38 ס׳ 4). */
export const STAGE_FILTERED_REASON_PREFIX = 'הפרק סונן כי לא נבחר שלב';

/**
 * פרק ״לבחירה״ (D38): הסעיפים אינם בביקור עד שהבקר בוחר בפרק — בפתיחה (OPTIONAL_CHOSEN_REASON)
 * או במהלך הביקור (ITEM_ADDED_REASON). שום סעיף אינו מסומן ״תקין״ מעצמו (A-17).
 */
export const OPTIONAL_CHAPTER_REASON = 'פרק לבחירה — לא נבחר לביקור. אפשר להוסיף.';
export const OPTIONAL_CHOSEN_REASON = 'נבחר בפתיחת הביקור';

export function decideApplicability(
  rule: ApplicabilityRule,
  facts: SiteFacts,
): ApplicabilityDecision {
  if (rule.kind === 'always') return { applicable: true, reason: 'חל בכל ביקור' };
  if (rule.kind === 'stage_kind_any') {
    const chosen = facts.activeStageKinds;
    if (chosen === 'unknown' || chosen.length === 0)
      return { applicable: true, reason: NO_STAGES_REASON };
    const matched = rule.kinds.find((k) => chosen.includes(k));
    return matched !== undefined
      ? {
          applicable: true,
          reason: `הפרק מוצג כי נבחר שלב ׳${STAGE_KIND_LABEL[matched]}׳ (כלל מוצר)`,
        }
      : {
          applicable: false,
          reason: `${STAGE_FILTERED_REASON_PREFIX} ${rule.label} — אפשר להחזיר (כלל מוצר)`,
        };
  }
  if (rule.kind === 'optional') return { applicable: false, reason: OPTIONAL_CHAPTER_REASON };
  const value = facts[rule.fact];
  if (value === 'unknown') return { applicable: true, reason: UNKNOWN_FACT_REASON };
  return value === rule.equals
    ? { applicable: true, reason: `מתקיים: ${rule.label}` }
    : { applicable: false, reason: `לא מתקיים: ${rule.label}` };
}

/** קורא תנאי שנשמר במסד (jsonb). תנאי לא מוכר — מוצג, לא מסונן. */
export function parseApplicability(raw: unknown): ApplicabilityRule {
  if (typeof raw === 'object' && raw !== null && 'kind' in raw) {
    const r = raw as Record<string, unknown>;
    if (r.kind === 'always') return { kind: 'always' };
    if (
      r.kind === 'fact' &&
      typeof r.fact === 'string' &&
      typeof r.equals === 'string' &&
      typeof r.label === 'string' &&
      (APPLICABILITY_FACTS as readonly string[]).includes(r.fact)
    ) {
      return raw as ApplicabilityRule;
    }
    if (
      r.kind === 'stage_kind_any' &&
      typeof r.label === 'string' &&
      Array.isArray(r.kinds) &&
      r.kinds.length > 0 &&
      r.kinds.every((k) => STAGE_KINDS.includes(k as StageKind))
    ) {
      return { kind: 'stage_kind_any', kinds: r.kinds as StageKind[], label: r.label };
    }
    // ״לבחירה״ (D38) — אותם תנאים כמו private.check_template_content: תווית לא ריקה, עד 80 תווים.
    if (
      r.kind === 'optional' &&
      typeof r.label === 'string' &&
      r.label.trim() !== '' &&
      r.label.length <= 80
    ) {
      return { kind: 'optional', label: r.label };
    }
  }
  return { kind: 'always' };
}

// --- פרקים לפי שלב העבודה (D38) ------------------------------------------------------

/** סוג פרק: ״תמיד״ · ״לפי שלב״ · ״לבחירה״ (D38 ס׳ 1). */
export type ChapterInclusion = 'always' | 'stage' | 'optional';

/**
 * סוג הפרק לפי כללי התחולה של סעיפיו: כולם ״לבחירה״ ← לבחירה; יש כלל שלב ← לפי שלב;
 * אחרת ← תמיד (גם פרק ״תמיד״ שיש בו סעיפים לפי עובדה). פרק בלי סעיפים — ״תמיד״.
 */
export function chapterInclusion(
  questions: ReadonlyArray<{ applicability: unknown }>,
): ChapterInclusion {
  const rules = questions.map((q) => parseApplicability(q.applicability));
  if (rules.length > 0 && rules.every((r) => r.kind === 'optional')) return 'optional';
  if (rules.some((r) => r.kind === 'stage_kind_any')) return 'stage';
  return 'always';
}

/** שם השלב של פרק ״לפי שלב״ — התווית של כלל השלב הראשון בפרק; לפרק אחר — null. */
export function chapterStageLabel(
  questions: ReadonlyArray<{ applicability: unknown }>,
): string | null {
  for (const q of questions) {
    const r = parseApplicability(q.applicability);
    if (r.kind === 'stage_kind_any') return r.label;
  }
  return null;
}

/** הנוסח של סוג הפרק בצ׳יפ: ״תמיד״ · ״לפי שלב: שלד״ · ״לבחירה״. */
export function chapterInclusionLabel(kind: ChapterInclusion, stageLabel: string | null): string {
  if (kind === 'optional') return 'לבחירה';
  if (kind === 'stage') return stageLabel === null ? 'לפי שלב' : `לפי שלב: ${stageLabel}`;
  return 'תמיד';
}

/**
 * ההחלטה בפתיחת ביקור (D38 ס׳ 2): סעיף של פרק ״לבחירה״ שהבקר סימן — בתחולה, עם
 * OPTIONAL_CHOSEN_REASON; כל השאר — בדיוק decideApplicability.
 */
export function decideOpening(
  rule: ApplicabilityRule,
  facts: SiteFacts,
  chapterChosen: boolean,
): ApplicabilityDecision {
  if (rule.kind === 'optional' && chapterChosen)
    return { applicable: true, reason: OPTIONAL_CHOSEN_REASON };
  return decideApplicability(rule, facts);
}

// --- הסרה והחזרה של סעיפים (D21) --------------------------------------------------

/** סיבות ברירת המחדל שנרשמות ב־visit_item.reason — הממשק והדוח מזהים לפיהן הסרה ידנית. */
export const ITEM_REMOVED_REASON = 'הוסר מהביקור על ידי הבקר';
export const ITEM_RESTORED_REASON = 'הוחזר לביקור על ידי הבקר';
/** D38: פרק שהבקר הוסיף בחלונית ״בחירת פרקים״ במהלך הביקור. */
export const ITEM_ADDED_REASON = 'נוסף לביקור בידי הבקר';

/**
 * סוג של פרק שלא נכלל בביקור, לפי הסיבות שנרשמו בסעיפיו — בדוח אין את כללי התחולה, רק
 * את הסיבות (D38 ס׳ 4). כל הסיבות ״הוסר״ ← הוסר; כולן ״לבחירה״ ← לבחירה; כולן סינון לפי
 * שלב ← לפי שלב; אחרת (עובדה מתיק האתר, או שילוב) ← סונן.
 */
export type ExcludedChapterKind = 'optional' | 'stage' | 'removed' | 'filtered';

export function excludedChapterKind(reasons: readonly string[]): ExcludedChapterKind {
  if (reasons.length === 0) return 'filtered';
  if (reasons.every((r) => r === ITEM_REMOVED_REASON)) return 'removed';
  if (reasons.every((r) => r === OPTIONAL_CHAPTER_REASON)) return 'optional';
  if (reasons.every((r) => r.startsWith(STAGE_FILTERED_REASON_PREFIX))) return 'stage';
  return 'filtered';
}

export const EXCLUDED_CHAPTER_KIND_LABEL: Record<ExcludedChapterKind, string> = {
  optional: 'לבחירה',
  stage: 'לפי שלב',
  removed: 'הוסר',
  filtered: 'סונן',
};

// --- מספור קריא (D22) ------------------------------------------------------------

/**
 * מספור קריא ״5.2״ לצד הקוד הפנימי (``C5-02``): הפרק לפי מקומו ברשימת הפרקים (1, 2, …),
 * הסעיף לפי מקומו בין הסעיפים **שבתחולה** בפרק. סעיף שאינו בתחולה — בלי מספר (הקוד
 * נשאר המזהה). אותה פונקציה לממשק ולדוח.
 */
export function readableNumbering(input: {
  chapters: ReadonlyArray<{ id: string }>;
  questions: ReadonlyArray<{ id: string; chapterId: string }>;
  items: ReadonlyArray<{ questionId: string; applicable: boolean }>;
}): { chapter: Map<string, number>; question: Map<string, string> } {
  const applicable = new Map(input.items.map((i) => [i.questionId, i.applicable]));
  const chapter = new Map<string, number>();
  const question = new Map<string, string>();
  input.chapters.forEach((c, ci) => {
    chapter.set(c.id, ci + 1);
    let n = 0;
    for (const q of input.questions) {
      if (q.chapterId !== c.id || applicable.get(q.id) === false) continue;
      n += 1;
      question.set(q.id, `${ci + 1}.${n}`);
    }
  });
  return { chapter, question };
}

// --- סיכום ביקור --------------------------------------------------------------

export interface EngineChapter {
  id: string;
  code: string;
  title: string;
  scheme: AnswerScheme;
}

export interface EngineQuestion {
  id: string;
  chapterId: string;
  code: string;
  title: string;
}

export interface EngineItem {
  questionId: string;
  applicable: boolean;
  reason: string;
}

export interface EngineAnswer {
  questionId: string;
  state: AnswerValue | null;
}

export interface EngineFinding {
  id: string;
  questionId: string | null;
  severity: StoredSeverity | null;
  withdrawn: boolean;
}

export interface ChapterSummary {
  chapter: EngineChapter;
  ok: number;
  defect: number;
  notRelevant: number;
  notChecked: number;
  filtered: number;
}

export interface VisitSummary {
  chapters: ChapterSummary[];
  totals: Omit<ChapterSummary, 'chapter'>;
  /** סעיפים חלים שלא סומנו — מוצגים, לא ממולאים. */
  notChecked: EngineQuestion[];
  /** סעיפים שסוננו, עם הסיבה — מוצגים בנפרד ובדוח. */
  filtered: Array<{ question: EngineQuestion; reason: string }>;
  /** מה חוסם הגשה — אותם כללים כמו בשרת. */
  blockers: string[];
  /**
   * סעיפים שסומנו כליקוי ואין להם ממצא ממוספר (D23) — מידע, לא חסם: הסימון, ההערה
   * והתמונות הם הליקוי, והוא מופיע בדוח בשורת הסעיף.
   */
  defectsWithoutFinding: EngineQuestion[];
}

export function summarizeVisit(input: {
  chapters: EngineChapter[];
  questions: EngineQuestion[];
  items: EngineItem[];
  answers: EngineAnswer[];
  findings: EngineFinding[];
}): VisitSummary {
  const item = new Map(input.items.map((i) => [i.questionId, i]));
  const answer = new Map(input.answers.map((a) => [a.questionId, a.state]));
  const live = input.findings.filter((f) => !f.withdrawn);

  const notChecked: EngineQuestion[] = [];
  const filtered: VisitSummary['filtered'] = [];
  const chapters = input.chapters.map((chapter) => {
    const summary: ChapterSummary = {
      chapter,
      ok: 0,
      defect: 0,
      notRelevant: 0,
      notChecked: 0,
      filtered: 0,
    };
    for (const q of input.questions.filter((x) => x.chapterId === chapter.id)) {
      const it = item.get(q.id);
      if (it !== undefined && !it.applicable) {
        summary.filtered += 1;
        filtered.push({ question: q, reason: it.reason });
        continue;
      }
      const state = answer.get(q.id) ?? null;
      if (state === null) {
        summary.notChecked += 1;
        notChecked.push(q);
      } else if (state === 'ok') summary.ok += 1;
      else if (state === 'not_relevant') summary.notRelevant += 1;
      else summary.defect += 1;
    }
    return summary;
  });

  const totals = chapters.reduce(
    (t, c) => ({
      ok: t.ok + c.ok,
      defect: t.defect + c.defect,
      notRelevant: t.notRelevant + c.notRelevant,
      notChecked: t.notChecked + c.notChecked,
      filtered: t.filtered + c.filtered,
    }),
    { ok: 0, defect: 0, notRelevant: 0, notChecked: 0, filtered: 0 },
  );

  const blockers: string[] = [];
  const questionById = new Map(input.questions.map((q) => [q.id, q]));
  const defectsWithoutFinding: EngineQuestion[] = [];
  for (const [questionId, state] of answer) {
    const q = questionById.get(questionId);
    const applicable = item.get(questionId)?.applicable !== false;
    if (
      q !== undefined &&
      applicable &&
      isDefect(state) &&
      !live.some((f) => f.questionId === questionId)
    )
      defectsWithoutFinding.push(q);
  }
  const noSeverity = live.filter((f) => f.severity === null).length;
  if (noSeverity > 0) blockers.push(`${noSeverity} ממצאים ללא רמת חומרה`);

  return { chapters, totals, notChecked, filtered, blockers, defectsWithoutFinding };
}
